import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import cancelIcon from '../../../shared/assets/svg/cancel_icon.svg';
import { getAllBlocks, getAllUnitsByProjectId } from '../../services/api/implementation/impl';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Loader from '../../../shared/components/ui/Loader';
import { stagePercentCapMessage, isAdvanceStageName } from '../../utils/stagePercentCap';
import CheckboxMultiSelect from '../ui/CheckboxMultiSelect';

// Every block/unit is created with an Advance stage (10% by default). The server
// counts it toward the 100% cap, so the bulk form has to carry it too.
const DEFAULT_ADVANCE_PERCENTAGE = 10;

const AddBulkPaymentStage = ({
  handleOnPaymentStage,
  onClose,
  isSubmitting,
  projectId,
  isIndependent = false
}) => {
  const userId = Cookies.get('userId');
  const [totalPercentage, setTotalPercentage] = useState(0);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [availableBlocks, setAvailableBlocks] = useState([]);

  const {
    data: blocksDyna,
    error: blocksError,
    isLoading: isLoadingItems
  } = useQuery({
    queryKey: isIndependent ? ['units', projectId] : ['blocks', projectId],
    queryFn: () => (isIndependent ? getAllUnitsByProjectId(projectId) : getAllBlocks(projectId)),
    enabled: !!projectId
  });

  // React Query v5 dropped onError on useQuery, so surface load failures here.
  useEffect(() => {
    if (!blocksError) return;
    toast.error(
      blocksError?.response?.data?.message ||
        blocksError?.message ||
        `Failed to fetch ${isIndependent ? 'units' : 'blocks'}`
    );
  }, [blocksError, isIndependent]);

  useEffect(() => {
    mixpanel.track('Add Bulk Payment Stage Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (!blocksDyna || blocksDyna.length === 0) {
      setAvailableBlocks([]);
      return;
    }

    const options = [];
    if (isIndependent) {
      for (const item of blocksDyna) {
        if (item.block?.type !== 'Independent') continue;
        options.push({
          value: item.id,
          label: item.unitName
            ? `${item.unitName}${item.block?.name ? ` (${item.block.name})` : ''}`
            : `Unit ${item.id}`
        });
      }
    } else {
      for (const item of blocksDyna) {
        if (item.type && item.type !== 'Group') continue;
        options.push({
          value: item.id,
          label: item.name || `Block ${item.id}`
        });
      }
    }

    options.sort((a, b) => a.label.localeCompare(b.label));
    setAvailableBlocks(options);
  }, [blocksDyna, isIndependent]);

  const { register, handleSubmit, control, watch, setValue } = useForm({
    defaultValues: {
      payment: [
        { name: 'Advance', percentage: DEFAULT_ADVANCE_PERCENTAGE, unitwise: false },
        { name: 'Payment 1', percentage: '', unitwise: false }
      ]
    }
  });

  const {
    fields: paymentStageFields,
    append: appendPaymentStage,
    remove: removePaymentStage
  } = useFieldArray({
    control,
    name: 'payment'
  });

  const watchPaymentFields = watch('payment') || [];

  useEffect(() => {
    const total = watchPaymentFields.reduce(
      (acc, curr) => acc + (parseFloat(curr.percentage) || 0),
      0
    );
    setTotalPercentage(total);
  }, [watchPaymentFields]);

  const handlePercentageChange = (index, event) => {
    // Percentages are stored as whole numbers.
    let value = Math.round(parseFloat(event.target.value));

    if (value > 100) {
      value = 100;
    } else if (value < 0 || isNaN(value)) {
      value = 0;
    }

    setValue(`payment.${index}.percentage`, value);

    const updatedPayment = [...watchPaymentFields];
    if (updatedPayment[index]) {
      updatedPayment[index].percentage = value;
    }
    const total = updatedPayment.reduce((acc, curr) => acc + (parseFloat(curr.percentage) || 0), 0);
    setTotalPercentage(total);
  };

  const handleAddNewStages = () => {
    if (totalPercentage >= 100) {
      toast.error("You've reached 100% of Payment");
      return;
    }

    appendPaymentStage({
      name: `Payment ${paymentStageFields.length}`,
      percentage: '',
      unitwise: false
    });
  };

  const handleOnSubmit = (data) => {
    if (selectedBlocks.length === 0) {
      toast.error(`Please select at least one ${isIndependent ? 'unit' : 'block'}`);
      return;
    }

    if (totalPercentage > 100) {
      toast.error(stagePercentCapMessage(totalPercentage));
      return;
    }

    if (totalPercentage !== 100) {
      toast.error('Total percentage must be exactly 100% before saving');
      return;
    }

    if (!data.payment.length) {
      toast.error('Please add at least one payment stage');
      return;
    }

    if (data.payment.some((stage) => !String(stage.name || '').trim())) {
      toast.error('All payment stages must have names');
      return;
    }

    if (data.payment.some((stage) => !stage.percentage || parseFloat(stage.percentage) <= 0)) {
      toast.error('All payment stages must have valid percentages');
      return;
    }

    const payload = {
      ...(isIndependent
        ? { unit_ids: selectedBlocks.map((block) => block.value) }
        : { block_ids: selectedBlocks.map((block) => block.value) }),
      stages: data.payment.map((stage) => ({
        name: String(stage.name).trim(),
        percentage: stage.percentage.toString(),
        ...(isIndependent ? {} : { unitwise: Boolean(stage.unitwise) })
      }))
    };

    handleOnPaymentStage(payload);
  };

  return (
    <div className="mt-10">
      <Loader isVisible={isLoadingItems} />
      {blocksError ? (
        <p className="mb-4 text-red-500">
          Failed to load {isIndependent ? 'units' : 'blocks'}. Close and try again.
        </p>
      ) : null}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {isIndependent ? 'Select units' : 'Select blocks'}
        </label>
        <CheckboxMultiSelect
          options={availableBlocks}
          value={selectedBlocks}
          onChange={setSelectedBlocks}
          className="basic-multi-select"
          classNamePrefix="select"
          isDisabled={isLoadingItems}
          isLoading={isLoadingItems}
          placeholder="Search or tick..."
        />
      </div>

      <div className="max-h-[30rem] overflow-y-auto hide-scrollbar">
        <table className="w-full table-auto border rounded-lg">
          <thead className="bg-gray-200 !text-gray-600 rounded-t-xl">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Percentage</th>
              {!isIndependent && <th className="px-4 py-2">Unitwise</th>}
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paymentStageFields?.map((field, index) => {
              const isAdvance = index === 0 && isAdvanceStageName(field.name);
              return (
                <tr key={field.id}>
                  <td className="px-4 py-2">
                    <FormInput
                      register={register}
                      register_key={`payment.${index}.name`}
                      type="text"
                      placeholder={`Payment ${index}`}
                      readOnly={isAdvance}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      {...register(`payment.${index}.percentage`)}
                      type="number"
                      placeholder="Percentage"
                      min="0"
                      max="100"
                      step="1"
                      onChange={(e) => handlePercentageChange(index, e)}
                      value={watchPaymentFields[index]?.percentage || ''}
                      className="border rounded p-2 w-full"
                    />
                  </td>
                  {!isIndependent && (
                    <td className="px-4 py-2">
                      <select
                        className="border rounded p-2 w-full"
                        value={watchPaymentFields[index]?.unitwise ? 'true' : 'false'}
                        disabled={isAdvance}
                        onChange={(e) =>
                          setValue(`payment.${index}.unitwise`, e.target.value === 'true')
                        }>
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </select>
                    </td>
                  )}
                  <td className="px-4">
                    {!isAdvance && (
                      <div className="ml-6" onClick={() => removePaymentStage(index)}>
                        <img className="cursor-pointer" src={cancelIcon} alt="cancel" />
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <p>Total Percentage: {totalPercentage.toFixed(2)}%</p>
        {totalPercentage > 100 && (
          <p className="text-red-500">{stagePercentCapMessage(totalPercentage)}</p>
        )}
        {totalPercentage !== 100 && totalPercentage <= 100 && (
          <p className="text-red-500">Total percentage must be exactly 100% before saving</p>
        )}
      </div>

      <div className="w-full flex justify-end items-center space-x-4 mt-8">
        <Button displayType="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          displayType="secondary"
          onClick={handleAddNewStages}
          disabled={totalPercentage >= 100}>
          Add New Stage
        </Button>
        <Button onClick={handleSubmit(handleOnSubmit)} disabled={isSubmitting}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default AddBulkPaymentStage;
